import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  LinearProgress,
  Chip
} from '@mui/material';
import {
  Mic,
  Stop,
  PlayArrow,
  Pause,
  Delete,
  Send,
  FiberManualRecord
} from '@mui/icons-material';

interface VoiceRecorderProps {
  open: boolean;
  onClose: () => void;
  onSendRecording: (audioBlob: Blob) => void;
}

const VoiceRecorder: React.FC<VoiceRecorderProps> = ({
  open,
  onClose,
  onSendRecording
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioUrl, setAudioUrl] = useState<string>('');

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  useEffect(() => {
    if (!open) {
      resetRecording();
    }
  }, [open]);

  const resetRecording = () => {
    setIsRecording(false);
    setIsPaused(false);
    setIsPlaying(false);
    setRecordingTime(0);
    setAudioBlob(null);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl('');
    }
    chunksRef.current = [];
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Unable to access microphone. Please check your permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause();
      setIsPaused(true);
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
      setIsPaused(false);
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    }
  };

  const playRecording = () => {
    if (audioRef.current && audioUrl) {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const pausePlayback = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  const handleSend = () => {
    if (audioBlob) {
      onSendRecording(audioBlob);
      onClose();
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <Mic />
            Voice Message
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, py: 2 }}>
            
            {/* Recording Status */}
            <Box sx={{ textAlign: 'center' }}>
              {isRecording && !isPaused && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <FiberManualRecord sx={{ color: 'red', fontSize: '12px' }} />
                  <Typography variant="body2" color="error">
                    Recording...
                  </Typography>
                </Box>
              )}
              
              {isPaused && (
                <Chip label="Paused" size="small" color="warning" sx={{ mb: 1 }} />
              )}
              
              {audioBlob && !isRecording && (
                <Chip label="Recording Complete" size="small" color="success" sx={{ mb: 1 }} />
              )}

              <Typography variant="h4" sx={{ fontFamily: 'monospace' }}>
                {formatTime(recordingTime)}
              </Typography>
            </Box>

            {/* Recording Progress */}
            {isRecording && (
              <LinearProgress 
                variant="indeterminate" 
                sx={{ width: '100%' }}
                color={isPaused ? 'warning' : 'primary'}
              />
            )}

            {/* Controls */}
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {!isRecording && !audioBlob && (
                <IconButton 
                  onClick={startRecording}
                  sx={{ 
                    bgcolor: 'primary.main', 
                    color: 'white',
                    width: 60,
                    height: 60,
                    '&:hover': { bgcolor: 'primary.dark' }
                  }}
                >
                  <Mic fontSize="large" />
                </IconButton>
              )}

              {isRecording && !isPaused && (
                <>
                  <IconButton 
                    onClick={pauseRecording}
                    sx={{ bgcolor: 'warning.main', color: 'white', '&:hover': { bgcolor: 'warning.dark' } }}
                  >
                    <Pause />
                  </IconButton>
                  <IconButton 
                    onClick={stopRecording}
                    sx={{ bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                  >
                    <Stop />
                  </IconButton>
                </>
              )}

              {isRecording && isPaused && (
                <>
                  <IconButton 
                    onClick={resumeRecording}
                    sx={{ bgcolor: 'success.main', color: 'white', '&:hover': { bgcolor: 'success.dark' } }}
                  >
                    <Mic />
                  </IconButton>
                  <IconButton 
                    onClick={stopRecording}
                    sx={{ bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                  >
                    <Stop />
                  </IconButton>
                </>
              )}

              {audioBlob && !isRecording && (
                <>
                  <IconButton 
                    onClick={isPlaying ? pausePlayback : playRecording}
                    sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }}
                  >
                    {isPlaying ? <Pause /> : <PlayArrow />}
                  </IconButton>
                  <IconButton 
                    onClick={resetRecording}
                    sx={{ bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                  >
                    <Delete />
                  </IconButton>
                </>
              )}
            </Box>

            {/* Instructions */}
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
              {!isRecording && !audioBlob && 'Click the microphone to start recording'}
              {isRecording && !isPaused && 'Recording... Click pause to pause or stop to finish'}
              {isRecording && isPaused && 'Recording paused. Click mic to resume or stop to finish'}
              {audioBlob && !isRecording && 'Click play to preview or trash to re-record'}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>
            Cancel
          </Button>
          {audioBlob && (
            <Button 
              onClick={handleSend} 
              variant="contained" 
              startIcon={<Send />}
            >
              Send Voice Message
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Hidden audio element for playback */}
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onEnded={handleAudioEnded}
          style={{ display: 'none' }}
        />
      )}
    </>
  );
};

export default VoiceRecorder;